<template>
  <div class="row no-gutters h-100 w-100" :class="{ 'messages-embedded': isEmbedded, 'conversation-selected': isConversationSelected }" v-show="!isEmbedded || hasContactsFunc() || searchQuery || initialStateLoaded" @click="handleGlobalClick">
    <div class="col-md-4 col-lg-3 d-flex flex-column col-messages-conversations">
      <!-- Empty state (no contacts at all) -->
      <div v-if="initialStateLoaded && !hasContacts && !currentContactId && !searchQuery" class="no-contacts-wrapper align-self-center m-auto p-4">
        <div class="empty-state empty-state-title d-flex flex-column justify-content-center align-items-center text-center">
          <div class="empty-icon mb-4">
            <svg width="120" height="120" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" class="messages-empty-icon">
              <!-- Chat bubble -->
              <path d="M16 12 C12 12 10 14 10 18 L10 48 C10 52 12 54 16 54 L24 54 L24 62 L34 54 L64 54 C68 54 70 52 70 48 L70 18 C70 14 68 12 64 12 Z" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" class="chat-bubble"></path>
              <!-- Dots inside bubble -->
              <circle cx="28" cy="33" r="3" fill="currentColor" opacity="0.3" class="chat-dot chat-dot-1"></circle>
              <circle cx="40" cy="33" r="3" fill="currentColor" opacity="0.3" class="chat-dot chat-dot-2"></circle>
              <circle cx="52" cy="33" r="3" fill="currentColor" opacity="0.3" class="chat-dot chat-dot-3"></circle>
              <!-- Pulsing heart (bottom-right corner) -->
              <g class="heart-icon">
                <circle cx="62" cy="60" r="11" fill="#fff" stroke="currentColor" stroke-width="2.5"></circle>
                <path d="M62 65 C58 62 57 59 59 57 C60 56 62 57 62 58 C62 57 64 56 65 57 C67 59 66 62 62 65 Z" fill="#e94442" class="heart-fill"></path>
              </g>
            </svg>
          </div>
          <h5 class="empty-title">{{ t('messages.no_contacts') }}</h5>
          <p class="empty-subtitle">{{ t('messages.no_contacts_description') }}</p>
          <a href="/directory/index" class="btn btn-primary btn-lg mt-2 btn-browse-people">
            <i class="fe fe-search mr-2"></i>{{ t('messages.browse_people') }}
          </a>
        </div>
      </div>
      <ConversationList
        v-else
        :conversations="conversationList"
        :current-contact-id="currentContactId"
        :typing="typingState"
        :unread-count="unreadState"
        :presence="presenceState"
        @select="handleSelectContact"
        @search="handleSearch"
      />
    </div>

    <div class="col-md-8 col-lg-9 col-messages-conversation">
      <!-- Loader state -->
      <div v-if="!initialStateLoaded" class="row align-self-center h-100 w-100" style="min-height: 200px;">
        <div class="dimmer active w-100 m-auto text-center">
          <div class="loader"></div>
          <div class="dimmer-content">
            {{ t('messages.loading') }}
          </div>
        </div>
      </div>

      <!-- Conversation empty placeholder (desktop only) -->
      <div v-if="!currentContactId && initialStateLoaded && hasContacts"
           class="conversation-empty d-none d-md-flex justify-content-center align-items-center h-100"
           style="min-height: 400px;">
        <div class="m-auto text-center">
          <div class="mb-4">
            <i class="fe fe-message-square text-gray" style="font-size: 64px;"></i>
          </div>
          <h4 class="text-bold mb-2">{{ t('messages.no_conversation_selected') }}</h4>
          <p class="text-muted mb-4">{{ t('messages.choose_conversation') }}</p>
          <div class="d-none d-lg-block">
            <div class="row justify-content-center">
              <div class="col-auto">
                <div class="feature-item">
                  <i class="fe fe-shield text-primary mb-2" style="font-size: 24px;"></i>
                  <div class="small">{{ t('messages.secure_messaging') }}</div>
                </div>
              </div>
              <div class="col-auto">
                <div class="feature-item">
                  <i class="fe fe-image text-success mb-2" style="font-size: 24px;"></i>
                  <div class="small">{{ t('messages.photo_sharing') }}</div>
                </div>
              </div>
              <div class="col-auto">
                <div class="feature-item">
                  <i class="fe fe-clock text-warning mb-2" style="font-size: 24px;"></i>
                  <div class="small">{{ t('messages.realtime_updates') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active conversation -->
      <div v-if="currentContactId" class="messages-conversation d-flex flex-column">
        <ConversationHeader
          :contact="currentContact"
          :online-status="currentContactOnlineStatus"
          :is-typing="isCurrentContactTyping"
          :last-online-text="(currentContact?.lastOnlineText as string)"
          :selected-messages-count="selectedMessagesCount"
          @back="handleBack"
          @open-menu="handleOpenMenu"
          @delete-selected-messages="handleDeleteSelectedMessages"
        />
        <MessageList 
          ref="messageListRef"
          :messages="currentMessages" 
          :contactId="currentContactId"
          :selection-mode="selectedMessages.size > 0"
          :selected-ids="selectedMessages"
          @restore="handleRestoreMessage"
          @reply="handleReplyToMessage"
          @edit="handleEditMessage"
          @delete-message="handleDeleteSingleMessage"
          @cancel-delete="handleCancelDeleteMessage"
          @timer-complete="doActualDeleteMessage"
          @react="handleReactToMessage"
          @toggle-select="handleToggleSelectMessage"
          @retry="handleRetryMessage"
        />
        <!-- Reply quote bar -->
        <div v-if="replyToMessage" class="reply-quote-bar d-flex align-items-center px-5 py-2 mb-1">
          <div class="reply-quote-line"></div>
          <div class="reply-quote-content flex-grow-1 ml-2">
            <div class="reply-quote-author small font-weight-bold">{{ replyToMessage.type === 'sent' ? t('messages.you') : (replyToMessage.user?.name || '') }}</div>
            <div class="reply-quote-text small text-muted text-truncate" v-html="getReplyPreview(replyToMessage)"></div>
          </div>
          <button type="button" class="btn btn-sm btn-link p-0 ml-2 text-muted" @click="replyToMessage = null">
            <i class="fe fe-x"></i>
          </button>
        </div>
        <!-- Post reply quote bar (ответ на пост из ленты — тот же reply-quote-bar) -->
        <div v-if="postQuote" class="reply-quote-bar d-flex align-items-center px-5 py-2 mb-1">
          <div class="reply-quote-line"></div>
          <img v-if="postQuote.photo" class="reply-quote-thumb ml-2" :src="postQuote.photo" alt="" loading="lazy">
          <div class="reply-quote-content flex-grow-1 ml-2">
            <div class="reply-quote-author small font-weight-bold">{{ postQuote.author }}</div>
            <div class="reply-quote-text small text-muted text-truncate">{{ postQuote.text }}</div>
          </div>
          <button type="button" class="btn btn-sm btn-link p-0 ml-2 text-muted reply-quote-close" :aria-label="t('messages.close')" :title="t('messages.close')" @click="postQuote = null">
            <i class="fe fe-x"></i>
          </button>
        </div>
        <!-- Edit mode bar -->
        <div v-if="editingMessage" class="edit-message-bar d-flex align-items-center px-5 py-2 mb-1">
          <div class="edit-content flex-grow-1 ml-2">
            <div class="edit-title small font-weight-bold text-primary">
              <template v-if="isReplyMessage(editingMessage.text)">
                <i class="fe fe-corner-up-left mr-1"></i>
                {{ t('messages.editing_reply') }}
              </template>
              <template v-else>
                {{ t('messages.editing_message') }}
              </template>
            </div>
            <div v-if="isReplyMessage(editingMessage.text)" class="edit-reply-preview small text-muted text-truncate" :title="extractQuotePreview(editingMessage.text)">
              {{ extractQuotePreview(editingMessage.text) || (t('messages.reply_to_prefix') + ' ' + getReplyPreview(editingMessage)) }}
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-link p-0 ml-2 text-muted" @click="cancelEdit">
            <i class="fe fe-x"></i>
          </button>
        </div>
        <!-- Delete pending bar -->
        <div v-if="deletePendingMessage && deletePanelVisible" class="delete-pending-bar  px-5 py-2 mb-1">
          <div class="delete-pending-left">
            <div class="delete-pending-line"></div>
            <div class="delete-pending-title small text-gray ml-2">
              <span v-if="deletePendingMessage._isMultiple">
                {{ t('messages.deleting_messages', { count: deletePendingMessage._ids?.length || 0 }) }}
              </span>
              <span v-else>
                {{ t('messages.deleting_message') }}
              </span>
            </div>
          </div>
          <div class="delete-pending-center">
            <UndoDeleteTimerInline
              :duration="5"
              @cancel="handleCancelDeleteMessage(deletePendingMessage)"
              @complete="doActualDeleteMessage(deletePendingMessage)"
            />
          </div>
          <div class="delete-pending-right">
            <button type="button" class="btn btn-sm btn-link p-0 text-muted" @click="handleCloseDeletePanel">
              <i class="fe fe-x"></i>
            </button>
          </div>
        </div>
        <MessageInput
          ref="messageInputRef"
          :current-contact-id="currentContactId"
          :is-user-blocked="isUserBlocked"
          :block-type="blockType"
          :edit-text="editText"
          :reply-to="replyToMessage"
          :text-required="postQuote !== null"
          @send="handleSend"
          @input="handleTypingInput"
          @unblock="handleUnblock"
        />
      </div>
    </div>

    <!-- Modals -->
    <ConversationMenuModal
      ref="menuModalRef"
      :contact="currentContact"
      :online-status="currentContactOnlineStatus"
      :is-user-blocked="isUserBlocked"
      :block-type="blockType"
      @report="handleReport"
      @gift="handleGift"
      @block="handleBlock"
      @clear-history="handleClearHistory"
      @delete-conversation="handleDeleteConversation"
    />

    <ReportModal
      ref="reportModalRef"
      :user-id="currentContactId!"
      @submit="handleReportSubmit"
      @close="handleReportClose"
    />
    
    <BanModal
      ref="banModalRef"
      :user-id="currentContactId!"
      :user="(currentContact as any) ?? undefined"
      @ban="handleBanSubmit"
      @close="handleBanClose"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, reactive, nextTick, watch } from 'vue';
import { useMessagesStore } from './stores/messages';
import type { OnlineStatus } from './stores/messages/types';
import { useI18n } from './composables/useI18n';
import ConversationList from './components/ConversationList.vue';
import ConversationHeader from './components/ConversationHeader.vue';
import MessageList from './components/MessageList.vue';
import MessageInput from './components/MessageInput.vue';
import ReportModal from './components/ReportModal.vue';
import BanModal from './components/BanModal.vue';
import ConversationMenuModal from './components/ConversationMenuModal.vue';
import UndoDeleteTimerInline from './components/UndoDeleteTimerInline.vue';
import { useUnifiedWebSocket } from './composables/useUnifiedWebSocket';
import { useTypingIndicator } from './composables/useTypingIndicator';
import { useMessageScroll } from './composables/useMessageScroll';
import { processReplyPreviewText, processQuoteText, filterQuotesOnly, extractQuotePreview } from './utils/textHelpers';
import { logger, getCurrentUserId } from './utils';

// Props
const props = defineProps<{
  embedded?: boolean;
  rootId?: string;
}>();

// Locate the root element this app instance was mounted into
const rootElement = computed(() => {
  if (props.rootId) {
    return document.getElementById(props.rootId);
  }
  // Fallback: support legacy single-id markup and both split ids
  return document.getElementById('messages-vue-root')
    || document.getElementById('messages-vue-root-standalone')
    || document.getElementById('messages-vue-root-modal');
});

// Read embedded from data attribute if not provided as prop
const isEmbedded = computed(() => {
  if (props.embedded !== undefined) return props.embedded;
  return rootElement.value?.getAttribute('data-embedded') === 'true';
});

// Read contact-id from data attribute (for opening specific conversation)
const initialContactId = computed(() => {
  const contactId = rootElement.value?.getAttribute('data-contact-id');
  logger.log('MessagesApp: initialContactId computed, contactId:', contactId);
  return contactId ? parseInt(contactId, 10) : null;
});

// Check if a conversation is selected
const isConversationSelected = computed(() => {
  return !!currentContactId.value;
});

const store = useMessagesStore();
const { connect, setEvents, subscribe, isConnected } = useUnifiedWebSocket();
const typing = useTypingIndicator();
const { t } = useI18n();

// WebSocket события chat/chat_message/message_read обрабатываются в useUnifiedWebSocket.
// message_edited и navigation_update централизованы в vue-entry.ts, чтобы избежать дублирования.
setEvents({
  onMessage: (_message: any) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  }
});

// Обработчики, которые нужно удалить при размонтировании.
// ВАЖНО: нельзя возвращать cleanup-функцию из onMounted — Vue её игнорирует.
// Все removeEventListener должны происходить в onBeforeUnmount / onUnmounted.
const handleMediaUploaded = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (detail?.contactId && currentContactId.value && String(detail.contactId) === String(currentContactId.value)) {
    logger.log('MessagesApp: Media uploaded, scrolling to bottom');
    store.ensureBottomPosition(currentContactId.value);
  }
};

// Handle modal open events from legacy app.js / Bootstrap modal
const handleOpenContactEvent = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  const contactId = detail?.contactId ? parseInt(detail.contactId, 10) : null;
  // Цитата поста от кнопки «ответить» в ленте; обычное открытие — цитату сбрасываем
  postQuote.value = detail?.postQuote?.id ? detail.postQuote : null;
  if (postQuote.value) replyToMessage.value = null;
  if (contactId) {
    logger.log('MessagesApp: open-contact event received:', contactId);
    store.setCurrentContactId(contactId);
    store.loadMessages(contactId).then(() => {
      logger.log('MessagesApp: Messages loaded, scrolling to bottom');
      store.ensureBottomPosition(contactId);
    });
    const modal = document.getElementById('messages-modal');
    if (modal) {
      modal.classList.add('conversation-selected');
    }
  } else {
    const modal = document.getElementById('messages-modal');
    if (modal) {
      modal.classList.remove('conversation-selected');
    }
  }
};

// Fallback-поллинг статуса блокировки на случай, если WebSocket notification не доехал.
// Основной канал обновлений — websocket:notification → handleBlockChanged (см. vue-entry.ts).
// Интервал 30с — достаточно редкий, чтобы не нагружать /block/check, но покрывать split-brain.
let blockStatusPollInterval: number | null = null;

const conversationList = computed(() => store.conversationOrder || []); // Используем сохраненный порядок с защитой от undefined
const currentMessages = computed(() => {
  const contactId = store.currentContactId;
  const messagesByChat = store.messagesByChat;
  
  logger.log('MessagesApp: currentMessages computed - contactId:', contactId, 'messagesByChat keys:', Object.keys(messagesByChat || {}));
  
  if (!contactId) {
    logger.log('MessagesApp: No contactId, returning empty array');
    return [];
  }
  
  const messages = (messagesByChat && messagesByChat[contactId]) || [];
  logger.log('MessagesApp: Found', messages.length, 'messages for contact', contactId);
  
  return messages;
});
const currentContactId = computed(() => store.currentContactId);
const currentContact = computed(() => {
  if (!store.currentContactId) return null;
  return (store.conversations && store.conversations[store.currentContactId]?.contact) || null;
});
const typingState = computed(() => store.typing || {});
const unreadState = computed(() => store.unread || {});
const presenceState = computed(() => store.presence || {});

// Mobile and selection state
const selectedMessages = reactive(new Set());
const selectedMessagesCount = computed(() => selectedMessages.size);
const replyToMessage = ref<any>(null);
// Цитата поста «ответ из ленты» — приходит через messages:open-contact (app.js)
const postQuote = ref<{ id: number; author: string; text: string; photo: string } | null>(null);
const messageInputRef = ref<any>(null);

// Editing state
const editingMessage = ref<any>(null);
const editText = ref('');

// Delete pending state
const deletePendingMessage = ref<any>(null);
const deletePanelVisible = ref(true);

// Blocking state
const isUserBlocked = ref(false);
const blockType = ref<'blocked_by_user' | 'user_blocked' | null>(null);

// Check if user is blocked
const checkUserBlocked = async (contactId: string | number): Promise<void> => {
  if (!contactId) {
    isUserBlocked.value = false;
    blockType.value = null;
    return;
  }

  // Skip network request while offline
  if (!navigator.onLine) return;
  
  try {
    const response = await fetch('/block/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: `contactId=${encodeURIComponent(String(contactId))}&_csrf=${encodeURIComponent(getCsrfToken())}`
    });
    
    const data = await response.json();
    isUserBlocked.value = data.isBlocked || false;
    blockType.value = data.blockType || null;
  } catch (error) {
    console.error('Failed to check block status:', error);
    isUserBlocked.value = false; // По умолчанию не заблокирован
    blockType.value = null;
  }
};

// State for loading and empty states (matches Angular)
const initialStateLoaded = ref(false);
const searchQuery = ref('');

// Computed for empty states
const hasContacts = computed(() => {
  return conversationList.value && conversationList.value.length > 0;
});

// Helper function for v-show
const hasContactsFunc = () => hasContacts.value;

// Mobile layout state
const mobileLayoutState = ref({
  isMobile: window.innerWidth < 768,
  sidebarActive: window.innerWidth < 768, // Активен на мобильных по умолчанию
  chatHidden: true,
  historyActive: false,
  ignoreNextPop: false
});

// DOM cache (matches Angular)
const domCache = ref({
  $body: null as HTMLElement | null,
  $sidebar: null as HTMLElement | null,
  $chat: null as HTMLElement | null,
  $primaryHeader: null as HTMLElement | null,
  $container: null as HTMLElement | null, // Контейнер для скроллинга
  $messageInput: null as HTMLElement | null
});

// Scrolling state (matches Angular) - упрощенная версия с useMessageScroll
const scrollState = ref({
  enableAutoScroll: true,
  scrollHandlerAttached: false
});

// Новый упрощенный composable для скроллинга
const containerRef = ref<HTMLElement | null>(null);

const { 
  scrollToBottom, 
  scrollToMessage,
  isAtBottom, 
  checkIsAtBottom,
  handleScroll 
} = useMessageScroll(
  containerRef,
  () => {
    // Автоматически читаем сообщения когда пользователь скроллит к низу
    if (store.currentContactId && checkIsAtBottom()) {
      logger.log('MessagesApp: Auto-reading messages due to scroll to bottom');
      store.readConversation(store.currentContactId);
    }
  }
);

// Упрощенные обертки для обратной совместимости
const immediateScrollToBottom = (): void => {
  scrollToBottom({ behavior: 'auto', force: true, waitForImages: false });
};

const setupScrollHandler = (): void => {
  if (!domCache.value.$container || scrollState.value.scrollHandlerAttached) return;

  domCache.value.$container.addEventListener('scroll', () => {
    handleScroll();
    // Обновляем enableAutoScroll на основе позиции
    scrollState.value.enableAutoScroll = checkIsAtBottom();
  });

  scrollState.value.scrollHandlerAttached = true;
};

// Handler for VueSidebar contact selection
const handleSidebarContactSelect = (event: CustomEvent): void => {
  logger.log('MessagesApp: Received vue-sidebar-contact-selected event', event.detail);
  if (event.detail?.contactId) {
    logger.log('MessagesApp: Calling handleSelectContact with', event.detail.contactId);
    handleSelectContact(event.detail.contactId);
  }
};

// History management functions
const isHistorySupported = (): boolean => {
  return typeof window !== 'undefined' &&
    window.history &&
    typeof window.history.pushState === 'function' &&
    typeof window.history.replaceState === 'function';
};

const ensureBaseHistoryState = (): void => {
  if (!isHistorySupported()) return;
  
  const currentState = window.history.state;
  if (!currentState || !currentState.messagesBase) {
    const baseState = { ...currentState, messagesBase: true };
    window.history.replaceState(baseState, document.title, window.location.href);
  }
};

const pushChatHistoryState = (contactId: string | number | null): void => {
  if (!isHistorySupported() || !mobileLayoutState.value.isMobile) {
    return;
  }
  
  const chatState = {
    messagesMobileChat: true,
    contactId: contactId || null,
    timestamp: Date.now()
  };
  
  if (mobileLayoutState.value.historyActive && window.history.state?.messagesMobileChat) {
    window.history.replaceState(chatState, document.title);
  } else {
    ensureBaseHistoryState();
    window.history.pushState(chatState, document.title);
    mobileLayoutState.value.historyActive = true;
  }
};

const exitChatHistoryState = (options: { ignoreNextPop?: boolean } = {}) => {
  if (!isHistorySupported() || !mobileLayoutState.value.isMobile) {
    return;
  }
  
  if (mobileLayoutState.value.historyActive && window.history.state?.messagesMobileChat) {
    if (options.ignoreNextPop) {
      mobileLayoutState.value.ignoreNextPop = true;
    }
    window.history.back();
    return;
  }
  
  mobileLayoutState.value.historyActive = false;
  mobileLayoutState.value.ignoreNextPop = false;
  ensureBaseHistoryState();
};

const handlePopState = (event: PopStateEvent) => {
  if (!mobileLayoutState.value.isMobile) {
    return;
  }
  
  if (mobileLayoutState.value.ignoreNextPop) {
    mobileLayoutState.value.ignoreNextPop = false;
    return;
  }
  
  const state = event.state || {};

  if (state.messagesMobileChat) {
    mobileLayoutState.value.historyActive = true;
    if (!currentContactId.value && state.contactId) {
      nextTick(() => {
        handleSelectContact(state.contactId);
      });
    }
    return;
  }

  if (mobileLayoutState.value.historyActive) {
    mobileLayoutState.value.historyActive = false;
    
    // Убедимся что DOM элементы закэшированы
    if (!domCache.value.$sidebar || !domCache.value.$chat) {
      cacheDOM();
    }
    
    nextTick(() => {
      // Сбрасываем контакт
      store.setCurrentContactId(null);
      selectedMessages.clear();
      
      // Показываем сайдбар с контактами
      if (domCache.value.$sidebar) {
        domCache.value.$sidebar.classList.add('active');
      }
      if (domCache.value.$chat) {
        domCache.value.$chat.classList.add('hide-messages');
      }
      
      // Обновляем состояние
      mobileLayoutState.value.sidebarActive = true;
      mobileLayoutState.value.chatHidden = true;
      updateMobileLayoutState();
    });
  }
};

// Modal refs
const menuModalRef = ref<InstanceType<typeof ConversationMenuModal> | null>(null);
const reportModalRef = ref<InstanceType<typeof ReportModal> | null>(null);
const banModalRef = ref<InstanceType<typeof BanModal> | null>(null);
const messageListRef = ref<InstanceType<typeof MessageList> | null>(null);

// Close menu modal whenever current contact changes (back to list or new contact selected)
// Sync conversation-selected on #messages-modal for CSS :has() rule
watch(currentContactId, (newId) => {
  menuModalRef.value?.close();
  const modal = document.getElementById('messages-modal');
  if (modal) {
    if (newId) {
      modal.classList.add('conversation-selected');
    } else {
      modal.classList.remove('conversation-selected');
    }
  }
});

// Sync the page-level online dot (directory, profile, etc.) with the freshly
// loaded contact data so it matches the conversation header.
watch(currentContact, (newContact, oldContact) => {
  if (!newContact?.id || !currentContactId.value) return;
  if (
    oldContact?.id === newContact.id &&
    oldContact?.last_seen === newContact.last_seen &&
    oldContact?.last_activity === newContact.last_activity
  ) {
    return;
  }
  const win = window as any;
  if (typeof win.updateUserOnlineStatus !== 'function') return;
  win.updateUserOnlineStatus(
    newContact.id,
    currentContactOnlineStatus.value,
    newContact.last_seen ?? null,
    newContact.last_activity ?? null,
  );
}, { flush: 'post' });

// Computed for header
const isCurrentContactOnline = computed(() => {
  if (!currentContactId.value) return false;
  const status = currentContactOnlineStatus.value;
  return status === 'online' || status === 'recently_online';
});

const currentContactOnlineStatus = computed((): OnlineStatus => {
  const id = currentContactId.value;
  if (!id) return 'offline';
  const presence = presenceState.value;
  if (presence && id in presence) {
    return presence[id as string] as OnlineStatus;
  }
  const fallback = currentContact.value?.online;
  if (typeof fallback === 'string' && ['online', 'recently_online', 'offline'].includes(fallback)) {
    return fallback as OnlineStatus;
  }
  if (fallback === true) return 'online';
  return 'offline';
});

const isCurrentContactTyping = computed(() => {
  if (!currentContactId.value) return false;
  const typing = typingState.value[currentContactId.value];
  return typing && Object.values(typing).some((v: any) => v?.isTyping);
});

// Update mobile layout state (matches Angular)
const updateMobileLayoutState = () => {
  const isMobile = window.innerWidth < 768;
  const isMobileChatActive = isMobile && !!currentContactId.value;
  
  mobileLayoutState.value.isMobile = isMobile;
  
  // Cache DOM elements if needed
  if (!domCache.value.$body) {
    domCache.value.$body = document.body;
  }
  if (!domCache.value.$primaryHeader) {
    domCache.value.$primaryHeader = document.querySelector('.header-main');
  }
  
  // Debug: log isEmbedded value
  logger.log('updateMobileLayoutState:', {
    isEmbedded: isEmbedded.value,
    isMobile,
    isMobileChatActive,
    hasPrimaryHeader: !!domCache.value.$primaryHeader
  });
  
  // Управление классами body и шапками
  // Шапки всегда видимы — скрытие убрано по запросу пользователя
  if (!isEmbedded.value) {
    if (domCache.value.$body) {
      domCache.value.$body.classList.remove('messages-mobile-chat-active');
    }
    if (domCache.value.$primaryHeader) {
      domCache.value.$primaryHeader.classList.remove('d-none');
    }
  } else {
    // Embedded: гарантированно очищаем классы, чтобы не скрывать шапки страницы
    if (domCache.value.$body) {
      domCache.value.$body.classList.remove('messages-mobile-chat-active');
    }
    if (domCache.value.$primaryHeader) {
      domCache.value.$primaryHeader.classList.remove('d-none');
    }

  }
  
  // Логика отображения для мобильной версии
  if (isMobile) {
    if (currentContactId.value) {
      // Показываем диалог, скрываем контакты
      mobileLayoutState.value.sidebarActive = false;
      mobileLayoutState.value.chatHidden = false;
    } else {
      // Показываем контакты, скрываем диалог
      mobileLayoutState.value.sidebarActive = true;
      mobileLayoutState.value.chatHidden = true;
    }
  } else {
    // Desktop: показываем оба
    mobileLayoutState.value.sidebarActive = false;
    mobileLayoutState.value.chatHidden = false;
  }
};

// Handle resize (matches Angular)
const handleResize = () => {
  updateMobileLayoutState();
};


// Handle online event: sync pending offline messages
const handleOnlineEvent = () => {
  logger.log('MessagesApp: Online event detected, syncing pending messages');
  store.syncPendingMessages();
};

// Start updating (matches Angular)
const startUpdating = () => {
  // In Angular, this starts periodic updates
  // For Vue, WebSocket handles real-time updates
  logger.log('Vue: WebSocket handles real-time updates');
  logger.log('Vue: navigation channel subscription is now automatic via WS_CONFIG.CHANNELS');
};

// Auto-scroll for new messages is handled by useMessageScroll inside MessageList.
// No additional watcher needed here — having one caused race conditions.
// Typing indicator is reactive via computed properties in ConversationHeader and ConversationList.

// Упрощенный mobile transition
const fastMobileTransition = (contactId: string | number): void => {
  const sidebar = domCache.value.$sidebar;
  const chat = domCache.value.$chat;
  
  if (!sidebar || !chat || !mobileLayoutState.value.isMobile) {
    return;
  }
  
  // Скроллим вниз перед transition
  immediateScrollToBottom();
  
  // Быстрая transition без анимации
  sidebar.style.transition = 'none';
  chat.style.transition = 'none';
  
  // Меняем visibility
  sidebar.classList.remove('active');
  chat.classList.remove('hide-messages');
  
  // Возвращаем normal transitions
  requestAnimationFrame(() => {
    setTimeout(() => {
      sidebar.style.transition = '';
      chat.style.transition = '';
    }, 50);
  });
  
  // History API
  pushChatHistoryState(contactId);
};

const handleSelectContact = (id: string | number): void => {
  logger.log('MessagesApp: handleSelectContact called with', id);
  logger.log('MessagesApp: Current messages before loading:', currentMessages.value.length);
  
  // 1. Проверяем статус блокировки (fallback если сервер не отправил WebSocket уведомление)
  void checkUserBlocked(id);
  
  // 2. Сразу устанавливаем контакт
  store.setCurrentContactId(id);
  
  logger.log('MessagesApp: Loading messages for contact', id);

  store.loadMessages(id).finally(() => {
    // Race-guard: пока шла загрузка, пользователь мог переключиться на другой контакт.
    // В этом случае скроллить и mark-read для старого диалога не нужно.
    if (String(store.currentContactId) !== String(id)) {
      logger.log('MessagesApp: contact changed during loadMessages, skipping post-load actions', {
        requested: id,
        current: store.currentContactId,
      });
      return;
    }

    logger.log('MessagesApp: Messages loaded, current messages:', currentMessages.value.length);

    // 3. Всегда скроллим вниз к последнему сообщению.
    // ВАЖНО: локальный ensureBottomPosition() использует useMessageScroll(containerRef),
    // а containerRef в MessagesApp.vue НЕ биндится к DOM (контейнер `.wrapper-items`
    // живёт внутри MessageList.vue). Поэтому используем store.ensureBottomPosition(),
    // который инкрементит store.scrollRequest — MessageList.vue реагирует на это через
    // watch и делает реальный scrollToBottomStable на своём контейнере.
    store.ensureBottomPosition(id);

    // Читаем сообщения один раз после загрузки
    store.readConversation(id);
  });
  
  // Navigation logic - разделение как в AngularJS
  if (mobileLayoutState.value.isMobile) {
    // На мобильной версии автоматически переходим к диалогу
    fastMobileTransition(id);
  } else {
    // На десктопе не используем History API
    ensureBaseHistoryState();
  }
  
  // Clear selection when changing conversation
  selectedMessages.clear();
  updateMobileLayoutState();
};

const handleSearch = (query: string) => {
  searchQuery.value = query;
  store.loadConversations(query);
};

const getReplyPreview = (item: any): string => {
  if (!item) return '';
  if (item.text?.startsWith('[gift]')) return '🎁 ' + t('messages.gift');
  // Миниатюра оригинала в превью цитаты (или иконка, если thumbnail недоступен)
  const att = item.attachments?.length
    ? (item.attachments.find((a: any) => a?.thumbnail || a?.url || a?.preview_url) || item.attachments[0])
    : null;
  const thumb = att ? String(att.thumbnail || att.url || att.preview_url || '').replace(/"/g, '%22') : '';
  const mediaIcon = item.attachments?.length
    ? (thumb ? `<img class="reply-quote-thumb" src="${thumb}" alt="">` : '<i class="fe fe-image mr-1"></i>')
    : '';
  if (!item.text && item.attachments?.length) return mediaIcon + t('messages.photo');
  return mediaIcon + processReplyPreviewText(item.text);
};

const handleRetryMessage = async (item: any) => {
  if (!store.currentContactId || !item) return;
  logger.log('MessagesApp: retry message', item.id);
  await store.retryFailedMessage(store.currentContactId, item.id);
};

const handleSend = async (payload: { text: string; files?: File[]; replyTo?: any }) => {
  // If in editing mode, save the edit instead of sending a new message
  if (editingMessage.value) {
    logger.debug('Editing mode - payload.text:', payload.text);
    logger.debug('Editing message:', editingMessage.value);
    await saveEdit(payload.text);
    return;
  }
  
  if (!store.currentContactId) return;
  
  // Fallback: check blocking status before sending (server doesn't send WebSocket notification)
  if (isUserBlocked.value) {
    logger.log('MessagesApp: User is blocked, preventing message send');
    showNotification(t('messages.cannot_send_blocked'), 'error');
    return;
  }

  // Ответ на пост: уходит на /feed/reply — карточку поста вкладывает backend,
  // текст цитаты не сериализуется в «> [reply:]» как у ответа на сообщение.
  // Модалку не закрываем: отправленное сообщение отображается в диалоге.
  if (postQuote.value) {
    const quote = postQuote.value;
    const sendContactId = store.currentContactId;
    const restoreDraft = () => {
      if (store.currentContactId === sendContactId) messageInputRef.value?.restoreText(payload.text, payload.files);
    };
    let replied = false;
    try {
      const body = 'postId=' + encodeURIComponent(String(quote.id)) +
                   '&message=' + encodeURIComponent(payload.text || '') +
                   '&_csrf=' + encodeURIComponent(getCsrfToken());
      const response = await fetch('/feed/reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body
      });
      const data = await response.json();
      if (data.success) {
        replied = true;
        postQuote.value = null;
        if (store.currentContactId) {
          await store.loadMessages(store.currentContactId);
          store.ensureBottomPosition(store.currentContactId);
        }
        // Лента: заблокировать повторный ответ + обновить дневной лимит
        window.dispatchEvent(new CustomEvent('feed:post-reply-sent', {
          detail: { postId: quote.id, quota: data.quota }
        }));
        // Прикреплённые файлы — вторым сообщением по обычному пути
        if (payload.files?.length) {
          await store.sendMessage({
            text: '',
            contactId: store.currentContactId,
            attachments: payload.files
          });
        }
      } else {
        restoreDraft();
        showNotification(data.error || data.message || t('messages.error'), 'error');
      }
    } catch (err) {
      console.error('MessagesApp: post reply error:', err);
      if (!replied) restoreDraft();
      showNotification(t('messages.error'), 'error');
    }
    return;
  }

  // Build text with reply quote if replying
  let finalText = payload.text;
  if (replyToMessage.value) {
    const reply = replyToMessage.value;
    let quotedText = '';
    
    // Обработка только текстовых сообщений и подарков для сохранения в БД
    if (reply.text) {
      quotedText = processQuoteText(reply.text.replace(/\[gift].*?\[\/gift]/g, '🎁'));
    }
    
    if (quotedText) {
      // Добавляем ID оригинального сообщения в цитату для кликабельности
      const replyId = reply.id || reply.messageId;
      finalText = `> [reply:${replyId}]${quotedText}\n${payload.text}`;
    } else if (reply.attachments?.length) {
      // Медиа-цитата: сохраняем только ID оригинала — иконку фото
      // подставит formatMessageText при рендере (поиск по ID в списке сообщений)
      const replyId = reply.id || reply.messageId;
      finalText = payload.text ? `> [reply:${replyId}]\n${payload.text}` : `> [reply:${replyId}]`;
    }
    replyToMessage.value = null;
  }
  
  try {
    const result = await store.sendMessage({
      text: finalText,
      contactId: store.currentContactId,
      attachments: payload.files,
      // Сохраняем оригинальный текст для отображения в сайдбаре
      displayText: payload.text
    });
    
    logger.log('MessagesApp: sendMessage result:', result);

    // Для медиа-сообщений дополнительно триггерим скролл через store,
    // поскольку изображения могут изменить высоту после рендера.
    if (payload.files && payload.files.length > 0 && store.currentContactId) {
      store.ensureBottomPosition(store.currentContactId);
    }
  } catch (error) {
    console.error('MessagesApp: sendMessage error:', error);
  }
};

const handleTypingInput = (isClearingAfterSend: boolean) => {
  if (!store.currentContactId) return;
  // Не отправляем typing при очистке сообщения после отправки
  if (!isClearingAfterSend) {
    typing.onInput(store.currentContactId);
  }
};

// Navigation back - разделение как в AngularJS
const handleBack = () => {
  menuModalRef.value?.close();
  if (mobileLayoutState.value.isMobile) {
    // Убедимся что DOM элементы закэшированы
    if (!domCache.value.$sidebar || !domCache.value.$chat) {
      cacheDOM();
    }
    
    // Показываем сайдбар с контактами
    if (domCache.value.$sidebar) {
      domCache.value.$sidebar.classList.add('active');
    }
    if (domCache.value.$chat) {
      domCache.value.$chat.classList.add('hide-messages');
    }
    
    // Сбрасываем контакт только после обновления UI
    store.setCurrentContactId(null);
    selectedMessages.clear();
    
    // Обновляем состояние мобильной навигации
    mobileLayoutState.value.sidebarActive = true;
    mobileLayoutState.value.chatHidden = true;
    
    // Выходим из состояния истории
    exitChatHistoryState({ ignoreNextPop: true });
    
    // Обновляем layout state для шапок
    updateMobileLayoutState();
  } else {
    // На десктопе просто сбрасываем контакт
    store.setCurrentContactId(null);
    selectedMessages.clear();
    updateMobileLayoutState();
  }
};

// Mobile navigation toggle function (matches Angular)
const toggleConversations = () => {
  if (!domCache.value.$sidebar || !domCache.value.$chat) {
    cacheDOM();
  }

  if (mobileLayoutState.value.isMobile) {
    if (domCache.value.$sidebar?.classList.contains('active')) {
      // Переход от списка контактов к диалогу
      domCache.value.$sidebar?.classList.remove('active');
      domCache.value.$chat?.classList.remove('hide-messages');
      if (currentContactId.value) {
        pushChatHistoryState(currentContactId.value);
      }
    } else {
      // Переход от диалога к списку контактов
      domCache.value.$sidebar?.classList.add('active');
      domCache.value.$chat?.classList.add('hide-messages');
      if (currentContactId.value) {
        exitChatHistoryState({ ignoreNextPop: true });
        store.setCurrentContactId(null);
      } else {
        ensureBaseHistoryState();
        updateMobileLayoutState();
      }
    }
  } else {
    // На десктопе просто переключаем видимость сайдбара
    if (domCache.value.$sidebar?.classList.contains('active')) {
      domCache.value.$sidebar?.classList.remove('active');
      domCache.value.$chat?.classList.remove('hide-messages');
    } else {
      domCache.value.$sidebar?.classList.add('active');
      domCache.value.$chat?.classList.add('hide-messages');
    }
    ensureBaseHistoryState();
  }

  updateMobileLayoutState();
};

// Header actions
const handleOpenMenu = (e?: MouseEvent) => {
  if (menuModalRef.value) {
    menuModalRef.value.open(e);
  }
};

const handleReport = () => {
  if (reportModalRef.value && currentContactId.value) {
    reportModalRef.value.openModal();
  }
};

const handleGift = () => {
  if (!currentContactId.value) return;
  const giftModal = document.getElementById('send-gift');
  if (giftModal && typeof (window as any).$ !== 'undefined') {
    // GiftForm::formName() returns '' so input name is 'toUserId'
    const toUserInput = (giftModal.querySelector('input[name="toUserId"]') ||
                         giftModal.querySelector('input[name="GiftForm[toUserId]"]')) as HTMLInputElement | null;
    if (toUserInput) {
      toUserInput.value = String(currentContactId.value);
    }
    const w = window as any;
    // Затемнение: bootstrap-бэкдроп (z 1040) скрыт под открытым #messages-modal
    // (z 1050) — пока gift открыт из мессенджера, вешаем флаг на body для
    // CSS-затемнения самой модалки (см. vue-messages.css)
    document.body.classList.add('gift-over-messages');
    w.$('#send-gift')
      .off('hidden.bs.modal.giftDim')
      .on('hidden.bs.modal.giftDim', () => document.body.classList.remove('gift-over-messages'));
    w.$('#send-gift').modal('show');
  } else if (currentContact.value?.url) {
    window.location.href = currentContact.value.url as string;
  }
};

// Bootbox-подтверждения: на десктопе центрируем относительно поля диалога
// мессенджера. DOM не переносим (иначе bootbox наследует стили
// #messages-modal и растягивается) — позиционируем внутренний .modal-dialog
// инлайном по центру rect колонки диалога. На мобайле — без изменений.
const confirmInDialog = (title: string, message: string, confirmLabel: string, onConfirm: () => void) => {
  const w = window as any;
  if (typeof w.bootbox === 'undefined') {
    if (confirm(message)) onConfirm();
    return;
  }
  const dlg = w.bootbox.confirm({
    // Длинный текст: "Вопрос? Пояснение." → две строки (bootbox-body рендерит
    // HTML; split по первому "? " работает для любого языка; нативный
    // confirm() ниже получает исходную строку без HTML)
    message: message.replace(/\? /, '?<br>'),
    title,
    // ВАЖНО: className в bootbox ЗАМЕНЯЕТ дефолтный "bootbox-confirm" —
    // без него слетают компактные стили подтверждений (vue-messages.css)
    className: 'bootbox-confirm messages-modal-confirm',
    buttons: {
      confirm: { label: confirmLabel, className: 'btn-danger' },
      cancel: { label: t('messages.cancel'), className: 'btn-secondary' }
    },
    callback: (result: boolean) => { if (result) onConfirm(); }
  });
  if (window.innerWidth >= 768) {
    const host = document.querySelector('#messages-modal .col-messages-conversation');
    const el = dlg && dlg[0];
    const inner = el && (el.querySelector('.modal-dialog') as HTMLElement | null);
    if (host && el && inner) {
      const r = host.getBoundingClientRect();
      inner.style.position = 'fixed';
      inner.style.top = `${Math.round(r.top + r.height / 2)}px`;
      inner.style.left = `${Math.round(r.left + r.width / 2)}px`;
      inner.style.transform = 'translate(-50%, -50%)';
      inner.style.margin = '0';
    }
  }
};

const handleBlock = () => {
  logger.debug('MessagesApp: handleBlock called, currentContactId:', currentContactId.value);
  if (!currentContactId.value) {
    logger.debug('MessagesApp: No currentContactId, returning');
    return;
  }

  const doBlock = async () => {
    logger.debug('MessagesApp: doBlock starting');
    try {
      const body = 'blockedUserId=' + encodeURIComponent(String(currentContactId.value)) +
                   '&_csrf=' + encodeURIComponent(getCsrfToken());

      logger.debug('MessagesApp: Sending fetch request to /block/toggle', { body });

      const response = await fetch('/block/toggle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body
      });

      logger.debug('MessagesApp: Fetch response received:', response);

      // Проверяем Content-Type ответа
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        showNotification(t('messages.server_error_invalid_response'), 'error');
        return;
      }

      const data = await response.json();
      if (data.success) {
        showNotification(data.message, 'success');
        // Обновляем статус блокировки
        if (currentContactId.value) {
          void checkUserBlocked(currentContactId.value);
        }
      } else {
        showNotification(data.message || t('messages.error'), 'error');
      }
    } catch (err) {
      console.error('Block toggle error:', err);
      // Показываем более понятное сообщение об ошибке
      const errorMessage = err instanceof SyntaxError 
        ? t('messages.server_error_try_again')
        : t('messages.failed_block_user');
      showNotification(errorMessage, 'error');
    }
  };

  confirmInDialog(
    t('messages.block_user'),
    t('messages.confirm_block_user'),
    t('messages.block_user'),
    doBlock
  );
};

const handleUnblock = () => {
  if (!currentContactId.value) return;

  const doUnblock = async () => {
    try {
      const body = 'blockedUserId=' + encodeURIComponent(String(currentContactId.value)) +
                   '&_csrf=' + encodeURIComponent(getCsrfToken());

      const response = await fetch('/block/toggle', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body
      });

      // Проверяем Content-Type ответа
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        showNotification(t('messages.server_error_invalid_response'), 'error');
        return;
      }

      const data = await response.json();
      if (data.success) {
        showNotification(data.message, 'success');
        // Обновляем статус блокировки
        if (currentContactId.value) {
          void checkUserBlocked(currentContactId.value);
        }
      } else {
        showNotification(data.message || t('messages.error'), 'error');
      }
    } catch (err) {
      console.error('Unblock error:', err);
      // Показываем более понятное сообщение об ошибке
      const errorMessage = err instanceof SyntaxError 
        ? t('messages.server_error_try_again')
        : t('messages.failed_unblock_user');
      showNotification(errorMessage, 'error');
    }
  };

  // Выполняем разблокировку сразу (без подтверждения)
  doUnblock();
};

const getCsrfToken = (): string => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') || '' : '';
};

const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
  const w = window as any;
  if (w.messenger?.[type]) {
    w.messenger[type](message);
  }
};

// Обработка WebSocket уведомлений о блокировках
const handleBlockChanged = (data: any) => {
  logger.log('MessagesApp: Block status changed via WebSocket', data);
  
  const fromUserId = data.from_user_id;
  const action = data.action;
  
  logger.log('MessagesApp: handleBlockChanged details', {
    fromUserId,
    action,
    currentContactId: currentContactId.value,
    matches: currentContactId.value && String(currentContactId.value) === String(fromUserId)
  });
  
  if (fromUserId && action) {
    // Показываем уведомление пользователю
    const message = action === 'blocked'
      ? t('messages.you_have_been_blocked')
      : t('messages.you_have_been_unblocked');

    showNotification(message, action === 'unblocked' ? 'success' : 'error');
    
    // Если это текущий контакт, немедленно обновляем статус блокировки
    // Используем строгое сравнение строк для избежания проблем с типами
    if (currentContactId.value && String(currentContactId.value) === String(fromUserId)) {
      logger.log('MessagesApp: Updating block status for current contact');
      void checkUserBlocked(currentContactId.value);
    } else {
      logger.log('MessagesApp: Current contact does not match blocked user, skipping update');
    }
  }
};

const handleClearHistory = () => {
  if (!currentContactId.value) return;

  const doClear = async () => {
    try {
      const body = 'contactId=' + encodeURIComponent(String(currentContactId.value)) +
                   '&_csrf=' + encodeURIComponent(getCsrfToken());

      const response = await fetch('/messages/delete-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body
      });

      const data = await response.json();
      if (data.success) {
        showNotification(t('messages.history_cleared'), 'success');
        // Clear messages locally but keep the contact/conversation
        store.messagesByChat[String(currentContactId.value)] = [];
      } else {
        showNotification(data.message || t('messages.error'), 'error');
      }
    } catch (err) {
      console.error('Clear history error:', err);
      showNotification(t('messages.error_clear_history'), 'error');
    }
  };

  confirmInDialog(
    t('messages.clear_history'),
    t('messages.confirm_clear_history'),
    t('messages.clear_history'),
    doClear
  );
};

const handleDeleteConversation = () => {
  if (!currentContactId.value) return;

  const doDelete = async () => {
    try {
      const body = 'contactId=' + encodeURIComponent(String(currentContactId.value)) +
                   '&_csrf=' + encodeURIComponent(getCsrfToken());

      const response = await fetch('/messages/delete-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body
      });

      const data = await response.json();
      if (data.success) {
        showNotification(data.message || t('messages.conversation_deleted'), 'success');
        await store.loadConversations();
      } else {
        showNotification(data.message || t('messages.error'), 'error');
      }
    } catch (err) {
      console.error('Delete conversation error:', err);
      showNotification(t('messages.error_delete_conversation'), 'error');
    }
  };

  confirmInDialog(
    t('messages.delete_conversation'),
    t('messages.confirm_delete_conversation'),
    t('messages.delete'),
    doDelete
  );
};

const handleDeleteSelectedMessages = () => {
  if (!selectedMessages.size) return;

  const doDelete = () => {
    // Store the selected messages for pending delete
    const ids = Array.from(selectedMessages);
    const chatId = currentContactId.value;
    if (!chatId || !store.messagesByChat[chatId]) return;

    // Get the messages data for potential restore
    const messagesToDelete = store.messagesByChat[chatId].filter((msg: any) => 
      ids.some((id: any) => String(id) === String(msg.id))
    );
    
    deletePendingMessage.value = {
      _isMultiple: true,
      _ids: ids,
      _messages: messagesToDelete.map((msg: any) => ({
        id: msg.id,
        _originalText: msg.text,
        _originalAttachments: msg.attachments ? [...msg.attachments] : [],
        post: msg.post || null,
        from_user_id: msg.from_user_id ?? null,
      })),
    };
    
    // Immediately show "You deleted this message" text for all selected messages while timer is running
    store.messagesByChat[chatId] = store.messagesByChat[chatId].map((msg: any) => {
      if (ids.some((id: any) => String(id) === String(msg.id))) {
        return {
          ...msg,
          text: t('messages.you_deleted_this_message'),
          attachments: [],
          _deletedLocally: true,
        };
      }
      return msg;
    });
    
    // Show the panel
    deletePanelVisible.value = true;
    selectedMessages.clear();
  };

  confirmInDialog(
    t('messages.delete_messages'),
    t('messages.confirm_delete_messages'),
    t('messages.delete'),
    doDelete
  );
};

const handleRestoreMessage = (message: any) => {
  if (!message || !message._deletedLocally) return;

  const chatId = currentContactId.value;
  if (!chatId || !store.messagesByChat[chatId]) return;

  store.messagesByChat[chatId] = store.messagesByChat[chatId].map((msg: any) => {
    if (String(msg.id) === String(message.id)) {
      const restored = { ...msg };
      if (restored._originalText !== undefined) restored.text = restored._originalText;
      if (restored._originalAttachments !== undefined) restored.attachments = restored._originalAttachments;
      delete restored._deletedLocally;
      delete restored._originalText;
      delete restored._originalAttachments;
      return restored;
    }
    return msg;
  });
};

const handleReplyToMessage = (item: any) => {
  replyToMessage.value = item;
};

// Helper function to extract reply quote from message text
const extractReplyQuote = (text: string): string | null => {
  if (!text) return null;
  const lines = text.split('\n');
  const quoteLines: string[] = [];
  
  for (const line of lines) {
    if (line.startsWith('> ') || line === '>') {
      quoteLines.push(line);
    } else if (quoteLines.length > 0) {
      // Stop when we hit non-quote lines
      break;
    }
  }
  
  return quoteLines.length > 0 ? quoteLines.join('\n') : null;
};

// Check if message is a reply
const isReplyMessage = (text: string): boolean => {
  if (!text) return false;
  return text.startsWith('> ');
};

// ===== Message Editing =====

const handleEditMessage = (item: any) => {
  logger.debug('handleEditMessage called with item:', {
    id: item.id,
    type: item.type,
    text: item.text,
    status: item.status,
    _isEditing: item._isEditing
  });
  
  // Проверки
  if (item.type !== 'sent') {
    showNotification(t('messages.cannot_edit_other'), 'error');
    return;
  }

  if (item._deletedLocally) {
    showNotification(t('messages.cannot_edit_deleted'), 'error');
    return;
  }

  // Открыть режим редактирования
  editingMessage.value = item;
  // Извлекаем только текст ответа без цитаты для редактирования
  editText.value = filterQuotesOnly(item.text) || item.text || '';
  logger.debug('Editing mode activated, editingMessage.id:', item.id, 'isReply:', isReplyMessage(item.text));
};

const cancelEdit = () => {
  editingMessage.value = null;
  editText.value = '';
};

const saveEdit = async (textFromInput?: string) => {
  logger.debug('saveEdit called', { textFromInput, editText: editText.value, editingMessage: editingMessage.value });

  if (!editingMessage.value) {
    logger.debug('saveEdit: no editingMessage, returning');
    return;
  }

  // Используем текст из поля ввода, если передан, иначе из editText
  const newText = (textFromInput || editText.value || '').trim();
  logger.debug('saveEdit: newText after trim:', newText);

  if (!newText) {
    logger.debug('saveEdit: newText is empty, returning');
    return;
  }

  const chatId = currentContactId.value;
  if (!chatId) {
    logger.debug('saveEdit: no chatId, returning');
    return;
  }
  
  // Проверяем, что текст действительно изменился
  const originalText = editingMessage.value.text || '';
  // Для сравнения используем только текст ответа (без цитаты)
  const originalReplyText = filterQuotesOnly(originalText) || originalText;
  logger.debug('saveEdit: text comparison', {
    originalText,
    originalReplyText,
    equal: newText === originalReplyText.trim(),
  });

  if (newText === originalReplyText.trim()) {
    // Текст не изменился - просто отменяем редактирование
    logger.debug('saveEdit: text not changed, canceling edit');
    cancelEdit();
    return;
  }
  
  // Сохраняем цитату при редактировании ответа
  const replyQuote = extractReplyQuote(originalText);
  const finalText = replyQuote ? `${replyQuote}\n${newText}` : newText;
  logger.debug('saveEdit: replyQuote and finalText', { replyQuote, finalText });

  // Если оптимистичное сообщение ещё не переиндексировалось на серверный id
  // (WebSocket-merge / reload не прошёл), но сервер уже подтвердил отправку —
  // используем `_serverId` для API-вызова, а в store продолжаем искать
  // сообщение по текущему (temp) id.
  const localId = editingMessage.value.id;
  const apiMessageId = editingMessage.value._serverId ?? localId;

  try {
    logger.debug('saveEdit: calling store.editMessage with:', {
      chatId,
      localId,
      apiMessageId,
      newText: finalText,
    });

    await store.editMessage(
      chatId,
      localId,
      finalText,
      apiMessageId,
    );

    showNotification(t('messages.message_edited'), 'success');
    cancelEdit();
  } catch (err: any) {
    console.error('[DEBUG] saveEdit error:', err);
    
    // Специальная обработка для известных ошибок.
    const msgStr: string = err.message || '';
    let errorCode: string | null = err.error_code || null;
    if (!errorCode) {
      if (msgStr.includes('edit_time_expired')) errorCode = 'edit_time_expired';
      else if (msgStr.includes('edit_read_forbidden')) errorCode = 'edit_read_forbidden';
    }

    if (errorCode === 'edit_time_expired') {
      showNotification(t('messages.edit_time_expired'), 'error');
    } else if (errorCode === 'edit_read_forbidden') {
      // Получатель успел прочитать сообщение между open-context-menu и save.
      showNotification(t('messages.edit_read_forbidden'), 'error');
    } else {
      showNotification(err.message || t('messages.error_edit_message'), 'error');
    }
  }
};

const handleToggleSelectMessage = (item: any) => {
  if (!item?.id) return;
  const idStr = String(item.id);
  if (selectedMessages.has(idStr)) {
    selectedMessages.delete(idStr);
  } else {
    selectedMessages.add(idStr);
  }
};

const handleDeleteSingleMessage = (item: any) => {
  if (!item?.id) return;
  if (deletePendingMessage.value?._isMultiple) return; // Multi-delete in progress
  if (deletePendingMessage.value && String(deletePendingMessage.value.id) === String(item.id)) return; // Already pending delete

  const chatId = currentContactId.value;
  if (!chatId || !store.messagesByChat[chatId]) return;

  // Store the message with original content for potential restore
  deletePendingMessage.value = {
    ...item,
    _originalText: item.text,
    _originalAttachments: item.attachments ? [...item.attachments] : [],
  };
  
  // Immediately show "You deleted this message" text while timer is running
  store.messagesByChat[chatId] = store.messagesByChat[chatId].map((msg: any) => {
    if (String(msg.id) === String(item.id)) {
      return {
        ...msg,
        text: t('messages.you_deleted_this_message'),
        attachments: [],
        _deletedLocally: true,
      };
    }
    return msg;
  });
  
  // Show the panel
  deletePanelVisible.value = true;

  // Close context menu if open
  if (messageListRef.value?.closeContextMenu) {
    messageListRef.value.closeContextMenu();
  }
};

const handleCancelDeleteMessage = (item: any) => {
  if (!item) return;

  const chatId = currentContactId.value;
  if (chatId && store.messagesByChat[chatId]) {
    // Handle multiple messages cancellation
    if (item._isMultiple && item._messages) {
      // Restore original content for all messages
      store.messagesByChat[chatId] = store.messagesByChat[chatId].map((msg: any) => {
        const msgData = item._messages.find((m: any) => String(m.id) === String(msg.id));
        if (msgData) {
          return {
            ...msg,
            text: msgData._originalText,
            attachments: msgData._originalAttachments,
            _deletedLocally: false,
          };
        }
        return msg;
      });
      selectedMessages.clear();
    } else if (item.id && item._originalText !== undefined) {
      // Handle single message cancellation - restore original content
      store.messagesByChat[chatId] = store.messagesByChat[chatId].map((msg: any) => {
        if (String(msg.id) === String(item.id)) {
          return {
            ...msg,
            text: item._originalText,
            attachments: item._originalAttachments || [],
            _deletedLocally: false,
          };
        }
        return msg;
      });
    }
  }

  // Clear the pending delete state
  deletePendingMessage.value = null;
  deletePanelVisible.value = true; // Reset for next time

  showNotification(t('messages.delete_cancelled'), 'info');
};

const handleCloseDeletePanel = () => {
  // Closing the panel = user chose not to undo → proceed with deletion immediately
  // (UndoDeleteTimerInline unmounts and cancels its timer when panel is hidden)
  if (deletePendingMessage.value) {
    doActualDeleteMessage(deletePendingMessage.value);
  } else {
    deletePanelVisible.value = false;
  }
};

const doActualDeleteMessage = async (item: any) => {
  if (!item) return;

  const chatId = currentContactId.value;
  if (!chatId) return;

  // Clear pending delete state
  deletePendingMessage.value = null;
  deletePanelVisible.value = true; // Reset for next time

  /* Лента: удалён собственный ответ на пост → кнопка возвращается в «Reply…»
     (без перезагрузки; feed.js восстанавливает data-post-* по карточке поста) */
  const notifyFeedPostReplyDeleted = (msg: any): void => {
    const post = msg?.post;
    const uid = getCurrentUserId();
    const isOwn = uid !== null && uid !== undefined && String(msg?.from_user_id) === String(uid);
    if (!post?.id || !isOwn) return;
    window.dispatchEvent(new CustomEvent('feed:post-reply-deleted', {
      detail: {
        postId: post.id,
        author: post.author || '',
        text: post.text || '',
        photo: post.photo || ''
      }
    }));
  };

  try {
    // Handle multiple messages deletion
    if (item._isMultiple && item._ids) {
      const ids = item._ids;
      
      // Use store action for offline queue support
      const result = await store.deleteMessages(chatId, ids);
      
      if (result.success) {
        showNotification(t('messages.messages_deleted'), 'success');
        selectedMessages.clear();
        (item._messages || []).forEach((m: any) => notifyFeedPostReplyDeleted(m));
      } else {
        showNotification(result.error || t('messages.error_delete_messages'), 'error');
        handleCancelDeleteMessage(item);
      }
      return;
    }

    // Handle single message deletion
    if (!item.id) return;

    // Use store action for offline queue support
    const result = await store.deleteMessage(chatId, item.id);
    
    if (result.success) {
      showNotification(t('messages.message_deleted'), 'success');
      notifyFeedPostReplyDeleted(item);
    } else {
      showNotification(result.error || t('messages.error_delete_message'), 'error');
      // Restore message on error
      handleCancelDeleteMessage(item);
    }
  } catch (err) {
    console.error('Delete message error:', err);
    showNotification(t('messages.error_delete_message'), 'error');
    // Restore message on error
    handleCancelDeleteMessage(item);
  }
};

const handleReactToMessage = async (item: any, emoji: string) => {
  if (!item?.id) return;

  const chatId = currentContactId.value;
  if (!chatId) return;

  // Для только что отправленных сообщений в store id остаётся временным,
  // а реальный server id хранится в _serverId. Используем его для API.
  const messageId = item._serverId || item.id;

  try {
    const result = await (store as any).toggleReaction(chatId, messageId, emoji);

    if (!result.success) {
      showNotification(result.error || t('messages.error'), 'error');
      return;
    }

    // Check for mutual reaction (pair creation logic)
    if (emoji === '❤️') {
      const reactions = result.reactions || [];
      const heartReaction = reactions.find((r: any) => r.emoji === '❤️');

      if (heartReaction && heartReaction.count >= 2) {
        logger.log('Mutual heart reaction detected - creating pair');
        await createMutualPair(messageId, chatId);
      }
    }

    logger.log('Reaction toggled:', emoji, 'on message:', messageId);
  } catch (err) {
    console.error('React error:', err);
    showNotification(t('messages.error'), 'error');
  }
};

// Create mutual pair when both users react with heart
const createMutualPair = async (messageId: number, contactId: string | number) => {
  try {
    const body = 'messageId=' + encodeURIComponent(String(messageId)) +
                 '&contactId=' + encodeURIComponent(String(contactId)) +
                 '&_csrf=' + encodeURIComponent(getCsrfToken());

    const response = await fetch('/messages/create-mutual-pair', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body
    });

    const data = await response.json();
    if (data.success) {
      // Show success notification
      showNotification(data.message || t('messages.pair_created'), 'success');
      
      // Optional: Add special visual effect or animation
      if (window.messenger?.success) {
        window.messenger.success(data.message || t('messages.you_created_pair'));
      }
    } else {
      logger.warn('Failed to create mutual pair:', data.message);
    }
  } catch (err) {
    console.error('Create mutual pair error:', err);
  }
};

// Modal handlers
const handleReportSubmit = (data: any) => {
  logger.log('Report submitted:', data);
  // Жалоба автоматически блокирует пользователя (ReportController::autoBlockReportedUser) —
  // обновляем статус блокировки, чтобы сразу disable ввод и показать статус в меню.
  // Тост с результатом показывает ReportModal (сообщение от сервера).
  if (data?.userId) {
    void checkUserBlocked(data.userId);
  }
};

const handleReportClose = () => {
  logger.log('Report modal closed');
};

const handleBanSubmit = (data: any) => {
  logger.log('User banned:', data);
  // Show success message using messenger system
  if (window.messenger?.success) {
    window.messenger.success(data.type === 'warning' ? t('messages.warning_sent_success') : t('messages.user_banned_success'));
  }
};

const handleBanClose = () => {
  logger.log('Ban modal closed');
};

// Cache DOM elements (matches Angular)
const cacheDOM = () => {
  const root = rootElement.value;
  if (root) {
    domCache.value.$sidebar = root.querySelector('.col-messages-conversations');
    domCache.value.$chat = root.querySelector('.col-messages-conversation');
    domCache.value.$container = root.querySelector('.wrapper-items');
    domCache.value.$messageInput = root.querySelector('.message-input');
  }
  domCache.value.$body = document.body;
  domCache.value.$primaryHeader = document.querySelector('.header-main');

  // Fallback: re-cache after Vue render in case elements weren't available yet
  nextTick(() => {
    if (!domCache.value.$sidebar || !domCache.value.$chat) {
      const root = rootElement.value;
      if (root) {
        domCache.value.$sidebar = root.querySelector('.col-messages-conversations');
        domCache.value.$chat = root.querySelector('.col-messages-conversation');
        domCache.value.$container = root.querySelector('.wrapper-items');
        domCache.value.$messageInput = root.querySelector('.message-input');
      }
    }
  });
};

// Глобальный обработчик кликов для закрытия контекстного меню сообщения.
// Должен быть объявлен ДО onMounted, т.к. используется внутри него.
const handleGlobalClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target) return;
  const conversationItems = target.closest('.conversation-items');
  const messageInput = target.closest('.conversation-input');
  const contextMenu = target.closest('.msg-ctx-popup');

  // Не закрывать при клике внутри popup (teleported to body)
  if (contextMenu) return;

  // Закрыть меню если:
  // 1. Клик по полю ввода
  // 2. Клик вне conversation-items
  // 3. Клик внутри conversation-items, но вне самого меню
  if (messageInput || !conversationItems || (conversationItems && !contextMenu)) {
    if (messageListRef.value && messageListRef.value.closeContextMenu) {
      messageListRef.value.closeContextMenu();
    }
  }
};

// BFCache: при возврате назад очищаем шапки, если модалка embedded
const handlePageShow = () => {
  if (isEmbedded.value) {
    document.body.classList.remove('messages-mobile-chat-active');
    document.body.classList.remove('modal-conversation-active');
    const ph = document.querySelector('.header-main');
    if (ph) ph.classList.remove('d-none');
  }
};

// Sync Vue state with Bootstrap Modal hide event.
// Without this, Vue retains stale currentContactId/selectedMessages after Bootstrap hides the modal.
const handleModalHidden = () => {
  store.setCurrentContactId(null);
  selectedMessages.clear();
  replyToMessage.value = null;
  postQuote.value = null;
  editingMessage.value = null;
  editText.value = '';
  deletePendingMessage.value = null;
  deletePanelVisible.value = true;
  menuModalRef.value?.close();
  const modal = document.getElementById('messages-modal');
  if (modal) modal.classList.remove('conversation-selected');
};

onMounted(() => {
  // Cache DOM elements
  cacheDOM();

  // Handle initial contact-id from data attribute (for opening specific conversation)
  const initialContactIdValue = initialContactId.value;
  if (initialContactIdValue) {
    logger.log('MessagesApp: Initial contact-id provided:', initialContactIdValue);
    store.setCurrentContactId(initialContactIdValue);
    // Load messages for this conversation and then scroll to bottom
    store.loadMessages(initialContactIdValue).then(() => {
      logger.log('MessagesApp: Messages loaded, scrolling to bottom');
      store.ensureBottomPosition(initialContactIdValue);
    });
    // Add conversation-selected class to modal for unified appearance
    const modal = document.getElementById('messages-modal');
    if (modal) {
      modal.classList.add('conversation-selected');
    }
  }

  window.addEventListener('messages:open-contact', handleOpenContactEvent);

  // Start WebSocket updates and subscribe to navigation channel
  startUpdating();

  // Add resize listener
  window.addEventListener('resize', handleResize);

  // Add listener for VueSidebar contact selection
  window.addEventListener('vue-sidebar-contact-selected', handleSidebarContactSelect as EventListener);

  // Add popstate listener for mobile navigation
  window.addEventListener('popstate', handlePopState);

  // BFCache cleanup listener
  window.addEventListener('pageshow', handlePageShow);

  // Слушатель события "media uploaded" от background upload'а — скроллим вниз.
  window.addEventListener('vue-messages-media-uploaded', handleMediaUploaded);

  // Глобальный обработчик кликов для закрытия контекстного меню сообщения.
  // Используем capture=true, чтобы перехватить клик до того, как меню сам его обработает.
  document.addEventListener('click', handleGlobalClick, true);

  // Fallback-поллинг статуса блокировки (основной канал — websocket:notification).
  // Пропускаем опрос, пока WebSocket подключен, чтобы не нагружать /block/check.
  blockStatusPollInterval = window.setInterval(() => {
    if (currentContactId.value && !isConnected.value) {
      void checkUserBlocked(currentContactId.value);
    }
  }, 30000);

  // Setup scroll handler (matches Angular)
  setupScrollHandler();


  // Initialize mobile layout
  nextTick(() => {
    handleResize();
    
    // On mobile, show sidebar by default
    if (mobileLayoutState.value.isMobile && !currentContactId.value) {
      if (domCache.value.$sidebar) {
        domCache.value.$sidebar.classList.add('active');
      }
      if (domCache.value.$chat) {
        domCache.value.$chat.classList.add('hide-messages');
      }
    }
  });
  
  // Load conversations and set initial state
  store.loadConversations().finally(async () => {
    initialStateLoaded.value = true;

    // If no contacts in embedded (modal) mode on mobile, open at half detent (50dvh)
    if (isEmbedded.value && !hasContacts.value && !currentContactId.value && window.innerWidth <= 768) {
      const modalEl = document.getElementById('messages-modal');
      if (modalEl) {
        modalEl.dataset.currentDetent = 'half';
        const content = modalEl.querySelector('.modal-content') as HTMLElement | null;
        if (content) {
          content.style.height = '50dvh';
        }
      }
    }

    // Restore pending offline messages from IndexedDB (survive page reload)
    try {
      await store.restorePendingMessages();
      // If we're back online, sync pending messages immediately
      if (navigator.onLine) {
        await store.syncPendingMessages();
      }
    } catch (e) {
      logger.warn('MessagesApp: Failed to restore/sync pending messages:', e);
    }
    
    // Восстановление контакта из URL (?contact=...) или history state (mobile SPA back/forward).
    // Только для standalone режима (не embedded модал), чтобы не скрывать шапки после F5.
    if (!isEmbedded.value) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlContactId = urlParams.get('contact');
      const historyContactId = window.history.state?.messagesMobileChat
        ? window.history.state?.contactId ?? null
        : null;
      const contactToOpen = urlContactId || historyContactId;
      if (contactToOpen) {
        handleSelectContact(contactToOpen);
      }
    }
  });
  
  // Listen for Bootstrap Modal hide event to sync Vue state
  const messagesModal = document.getElementById('messages-modal');
  if (messagesModal) {
    if ((window as any).jQuery) {
      (window as any).jQuery(messagesModal).on('hidden.bs.modal', handleModalHidden);
    } else {
      messagesModal.addEventListener('hidden.bs.modal', handleModalHidden);
    }
  }

  // Listen for online event to sync pending messages
  window.addEventListener('online', handleOnlineEvent);
  
  // Start WebSocket
  connect();
  
  // Subscribe to user channel for multi-device sync
  const currentUserId = (window as any).__currentUserId || (window as any).currentUser?.id;
  if (currentUserId && subscribe) {
    const userChannel = 'user.' + currentUserId;
    subscribe(userChannel);
    logger.log('MessagesApp: Subscribed to user channel for multi-device sync:', userChannel);

    // Subscribe to presence channel for real-time online status updates
    subscribe('presence');
    logger.log('MessagesApp: Subscribed to presence channel for online status updates');
  }
  
  // Make functions globally available for mobile navigation
  (window as any).vueMessagesToggleConversations = toggleConversations;
  (window as any).vueMessagesHandleBack = handleBack;
  
  // Make functions globally available for WebSocket notifications
  (window as any).__vueMessagesApp = {
    handleBlockChanged,
    checkUserBlocked,
    currentContactId: () => currentContactId.value,
  };
  
  logger.log('Vue Messages successfully initialized');
});

onUnmounted(() => {
  // Удаляем listeners и очищаем body классы
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('vue-sidebar-contact-selected', handleSidebarContactSelect as EventListener);
  window.removeEventListener('messages:open-contact', handleOpenContactEvent);
  window.removeEventListener('popstate', handlePopState);
  window.removeEventListener('pageshow', handlePageShow);
  window.removeEventListener('online', handleOnlineEvent);
  window.removeEventListener('vue-messages-media-uploaded', handleMediaUploaded);
  document.removeEventListener('click', handleGlobalClick, true);

  // Remove Bootstrap Modal hide listener
  const messagesModal = document.getElementById('messages-modal');
  if (messagesModal) {
    if ((window as any).jQuery) {
      (window as any).jQuery(messagesModal).off('hidden.bs.modal', handleModalHidden);
    } else {
      messagesModal.removeEventListener('hidden.bs.modal', handleModalHidden);
    }
  }

  if (blockStatusPollInterval !== null) {
    clearInterval(blockStatusPollInterval);
    blockStatusPollInterval = null;
  }

  document.body.classList.remove('messages-mobile-chat-active');
  document.body.classList.remove('modal-conversation-active');

  // Remove d-none from headers that may have been added in standalone mode
  const primaryHeader = document.querySelector('.header-main');
  if (primaryHeader) primaryHeader.classList.remove('d-none');

  // Remove conversation-selected from modal
  const modal = document.getElementById('messages-modal');
  if (modal) modal.classList.remove('conversation-selected');

  // Clean up global functions
  delete (window as any).vueMessagesToggleConversations;
  delete (window as any).vueMessagesHandleBack;
  delete (window as any).__vueMessagesApp;
});
</script>

<style scoped>
/* Reply quote bar */
.reply-quote-bar {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  max-width: 100%;
  overflow: hidden;
}

.reply-quote-line {
  width: 3px;
  min-height: 28px;
  border-radius: 2px;
  background: #467fcf;
  flex-shrink: 0;
}

.reply-quote-content {
  min-width: 0;
}

.reply-quote-author {
  color: #467fcf;
}

.reply-quote-close {
  min-width: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Миниатюра фото поста в баре цитаты (= .reply-quote-text .reply-quote-thumb) */
.reply-quote-bar .reply-quote-thumb {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

/* Тёмная тема — по референсу vue-messages.css .conversation-input .input-group:
   поверхность rgba(255,255,255,.06), автор — облегчённый синий,
   muted-текст/крестик — светло-серые (.text-muted в BS4 с !important) */
:is(.dark, .theme-dark, body.dark-theme, body.dark-mode)
  :is(.reply-quote-bar, .edit-message-bar, .delete-pending-bar) {
  background: rgba(255, 255, 255, .06);
}

:is(.dark, .theme-dark, body.dark-theme, body.dark-mode) .reply-quote-line {
  background: #7891ff;
}

:is(.dark, .theme-dark, body.dark-theme, body.dark-mode) .reply-quote-author {
  color: #8ba9f0;
}

:is(.dark, .theme-dark, body.dark-theme, body.dark-mode)
  :is(.reply-quote-bar, .edit-message-bar) .text-muted {
  color: rgba(233, 237, 245, .6) !important;
}

/* Edit message bar */
.edit-message-bar {
  background: #00000008;
  border-radius: 8px;
  max-width: 100%;
  overflow: hidden;
}

.edit-content {
  min-width: 0;
}

.edit-title {
  font-size: 12px;
  line-height: 1.2;
}

.edit-reply-preview {
  font-size: 11px;
  line-height: 1.3;
  margin-top: 2px;
  padding-left: 16px;
  border-left: 2px solid rgba(70, 127, 207, 0.3);
}

/* Delete pending bar */
.delete-pending-bar {
  background: #00000008;
  max-width: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
}

.delete-pending-left {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.delete-pending-line {
  width: 3px;
  min-height: 28px;
  border-radius: 2px;
  background: #cd2020;
  flex-shrink: 0;
}

.delete-pending-title {
  overflow: hidden;
}

.delete-pending-center {
  justify-self: center;
  display: flex;
  align-items: center;
}

.delete-pending-right {
  justify-self: end;
  display: flex;
  align-items: center;
}

/* Dark theme support for delete pending bar */
body.dark .delete-pending-bar,
body.dark-mode .delete-pending-bar,
body.dark-theme .delete-pending-bar,
.theme-dark .delete-pending-bar {
  background: rgba(255, 255, 255, 0.05);
}
</style>
